import React, { FC } from 'react';

import { Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { Box } from '@mui/system';

import {
  getBookedContractDates,
  getLongTermContractAttributes,
} from '@pharmaplan/common';

import useViewEditContract from '../../../../../../hooks/workshift/useViewEditContract';
import { useAppSelector } from '../../../../../../hooks/useAppSelector';
import {
  ChipType,
  TextFieldVariants,
} from '../../../../../../helpers/Constants';
import { getDayMonthDateYear } from '../../../../../Reports/helpers';
import { serverToday } from '../../../../../../helpers/Functions';
import DateTimeRange from '../../../../../common/DateTimeRange';
import CustomChip from '../../../../../common/CustomChip';
import strings from '../../../../../../localization';

import DateBox from './DateBox';
import styles from './styles';

interface IViewEditContractTime {
  formik: FormikProps<any>;
  setDisabled: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  disabledObject?: Record<string, boolean>;
}

export const nameTypes = {
  bookedStart: 'bookedStart',
  bookedEnd: 'bookedEnd',
  start: 'start',
  end: 'end',
  booked: 'booked',
  dayOff: 'dayOff',
  changed: 'changed',
} as const;

const ViewEditContractTime: FC<IViewEditContractTime> = ({
  formik,
  setDisabled,
  disabledObject,
}) => {
  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const { shiftContractAttributes } = longTermAttributes ?? {};
  const bookedDates = useAppSelector(getBookedContractDates);

  const { generateTimeIntervals } = useViewEditContract();

  const globalTimeHandler = (isStart?: boolean) =>
    (value?: string | Date | null) => {
      if (isStart) {
        formik.setValues({
          ...formik.values,
          ...generateTimeIntervals(shiftContractAttributes)(
            value as Date,
            formik.values.defaultEndTime,
          ),
        });
      } else {
        formik.setValues({
          ...formik.values,
          ...generateTimeIntervals(shiftContractAttributes)(
            formik.values.defaultStartTime,
            value as Date,
          ),
        });
      }
    };

  return (
    <Box sx={styles.container}>
      {!!bookedDates.length && (
        <Box sx={styles.bookedNoteContainer}>
          <Typography sx={styles.bookedNote}>
            {strings.bookedShiftsCannotBeEdited}
          </Typography>
        </Box>
      )}
      <Box sx={styles.subContainer}>
        <Box sx={styles.noteContainer}>
          <Typography sx={styles.title}>{strings.setDefaultTime}</Typography>
          <Typography sx={styles.note}>{strings.defaultTimeNote}</Typography>
        </Box>

        <Box sx={styles.timeContainer}>
          <DateTimeRange
            startName="defaultStartTime"
            endName="defaultEndTime"
            formik={formik}
            startPlaceholder="00:00"
            endPlaceholder="00:00"
            customEndHandler={globalTimeHandler()}
            customStartHandler={globalTimeHandler(true)}
            variant={TextFieldVariants.filled}
            horizontal={false}
            allowValidOnly={false}
            removeMargin
          />
        </Box>
      </Box>
      <Typography sx={styles.title}>{strings.chooseTime}</Typography>

      <Box sx={styles.dateBoxGrid}>
        {shiftContractAttributes?.map((interval, index) => {
          let Chip;
          const date = interval?.startDate?.split?.('T')?.[0];

          const isPast = interval?.startDate
            < (serverToday()?.toISO({ includeOffset: false }) ?? '');
          const isBooked = bookedDates?.includes(date);
          const hideDayOff = index === 0
            || index === shiftContractAttributes.length - 1
            || isBooked;

          let dateTimeName = {
            start: nameTypes.start as string,
            end: nameTypes.end as string,
          };

          if (isBooked) {
            dateTimeName = {
              start: nameTypes.bookedStart,
              end: nameTypes.bookedEnd,
            };
          }

          const disabled = isBooked || isPast;

          if (isPast) {
            Chip = (
              <CustomChip
                type={ChipType.notAvailable}
                label={strings.unfulfilled}
              />
            );
          }

          if (isBooked) {
            Chip = <CustomChip type="booked" label={strings.booked} />;
          }

          return (
            <DateBox
              setDisabled={setDisabled}
              disabledObject={disabledObject}
              disabled={disabled}
              key={date}
              changedName={`${date}|${nameTypes.changed}`}
              dayOffName={`${date}|${nameTypes.dayOff}`}
              Chip={Chip}
              startName={`${date}|${dateTimeName.start}`}
              title={`${getDayMonthDateYear(date)}`}
              endName={`${date}|${dateTimeName.end}`}
              formik={formik}
              hideDayOff={hideDayOff}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default ViewEditContractTime;
